import { useEffect } from 'react';
import { playSfx, primeSfx } from '../utils/sfx';

/** Interactive elements that get a click sound. */
const SEL = 'button, a[href], [role="button"], [role="tab"], [role="menuitem"], label[for], input[type="checkbox"], input[type="radio"], summary, [data-sfx]';

/**
 * Global UI sounds: one listener plays a short old-school Mario sound for every
 * button / link in the storefront — components do not have to do anything.
 * (A `<button>` gets the coin, an `<a>` gets the little two-note blip.)
 *
 * Per-element control:
 *   data-sfx="success"  → play that sound instead (e.g. the 1-UP jingle)
 *   data-sfx="none"     → stay silent (e.g. a button that plays its own sound)
 *
 * The full Mario library (`coin`, `jump`, `stomp`, `bump`, `oneUp`, `powerUp`,
 * `powerDown`, `fireball`, `pipe`, `blip`…) lives in `utils/sfx.js`.
 */
export default function SoundEffects() {
  useEffect(() => {
    // Warm the AudioContext on the very first gesture so the first click is crisp.
    const prime = () => { primeSfx(); window.removeEventListener('pointerdown', prime); };
    window.addEventListener('pointerdown', prime, { once: true });

    const onClick = (e) => {
      if (e.defaultPrevented === false && (e.button || 0) > 0) return;   // ignore right/middle click
      const el = e.target && e.target.closest ? e.target.closest(SEL) : null;
      if (!el) return;
      if (el.disabled || el.getAttribute('aria-disabled') === 'true') return;
      const name = el.getAttribute('data-sfx') || (el.tagName === 'A' ? 'tap' : 'click');
      if (name === 'none') return;
      playSfx(name);
    };

    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('pointerdown', prime);
    };
  }, []);

  return null;
}
